/**
 * The desktop render presets (MeasurementPanelView.Volumetric.cs, plan §5.4) and the six
 * bundled transfer functions (Assets/Resources/TransferFunctions/*.tf, verbatim).
 */
import type { TransferFunctionJson } from './tf';
import { LEAFLET_TISSUE_COLOR } from './leaflets';

export const TF_CARDIAC_VR: TransferFunctionJson = {
  version: 1,
  colourPoints: [
    { dataValue: 0.0, colourValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 } },
    { dataValue: 0.20844099, colourValue: { r: 0.3, g: 0.04, b: 0.02, a: 1.0 } },
    { dataValue: 0.3385013, colourValue: { r: 0.78, g: 0.2, b: 0.08, a: 1.0 } },
    { dataValue: 0.43496987, colourValue: { r: 1.0, g: 0.55, b: 0.18, a: 1.0 } },
    { dataValue: 0.4978467, colourValue: { r: 1.0, g: 0.85, b: 0.45, a: 1.0 } },
    { dataValue: 0.5, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 1.0, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
  ],
  alphaPoints: [
    { dataValue: 0.0, alphaValue: 0.0 },
    { dataValue: 0.15, alphaValue: 0.0 },
    { dataValue: 0.225, alphaValue: 0.05 },
    { dataValue: 0.3, alphaValue: 0.45 },
    { dataValue: 0.39, alphaValue: 0.92 },
    { dataValue: 0.5, alphaValue: 1.0 },
    { dataValue: 1.0, alphaValue: 1.0 },
  ],
};

export const TF_CARDIAC_VR_CUTAWAY: TransferFunctionJson = {
  version: 1,
  colourPoints: [
    { dataValue: 0.0, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 0.040789474, colourValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 } },
    { dataValue: 0.16973683, colourValue: { r: 1.0, g: 0.92438126, b: 0.0, a: 1.0 } },
    { dataValue: 0.25657895, colourValue: { r: 1.0, g: 0.5613208, b: 0.5753571, a: 1.0 } },
    { dataValue: 0.34435484, colourValue: { r: 0.6901961, g: 0.5882353, b: 0.5411765, a: 1.0 } },
    { dataValue: 0.39274195, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 0.49435484, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 0.5, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 1.0, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
  ],
  alphaPoints: [
    { dataValue: 0.0, alphaValue: 0.0 },
    { dataValue: 0.16693549, alphaValue: 0.5416667 },
    { dataValue: 0.18870968, alphaValue: 0.0 },
    { dataValue: 0.25887096, alphaValue: 0.09166667 },
    { dataValue: 0.30322582, alphaValue: 1.0 },
    { dataValue: 0.34758064, alphaValue: 1.0 },
    { dataValue: 0.36048388, alphaValue: 0.0 },
    { dataValue: 0.3763158, alphaValue: 0.0 },
    { dataValue: 0.4983871, alphaValue: 0.012360075 },
    { dataValue: 0.5, alphaValue: 0.9916667 },
    { dataValue: 1.0, alphaValue: 0.9916667 },
  ],
};

export const TF_CARDIAC_VR_CUTAWAY_CLEAN: TransferFunctionJson = {
  version: 1,
  colourPoints: [
    { dataValue: 0.0, colourValue: { r: 0, g: 0, b: 0, a: 1.0 } },
    { dataValue: 0.176801, colourValue: { r: 0, g: 0, b: 0, a: 1.0 } },
    { dataValue: 0.213431, colourValue: { r: 0.89, g: 0.7, b: 0.58, a: 1.0 } },
    { dataValue: 0.421001, colourValue: { r: 0.89, g: 0.7, b: 0.58, a: 1.0 } },
    { dataValue: 0.433211, colourValue: { r: 0.96, g: 0.94, b: 0.9, a: 1.0 } },
    { dataValue: 1.0, colourValue: { r: 0.96, g: 0.94, b: 0.9, a: 1.0 } },
  ],
  alphaPoints: [
    { dataValue: 0.0, alphaValue: 0.0 },
    { dataValue: 0.2, alphaValue: 0.0 },
    { dataValue: 0.25887096, alphaValue: 0.09166667 },
    { dataValue: 0.30322582, alphaValue: 1.0 },
    { dataValue: 0.34758064, alphaValue: 1.0 },
    { dataValue: 0.36048388, alphaValue: 0.0 },
    { dataValue: 0.3763158, alphaValue: 0.0 },
    { dataValue: 0.4983871, alphaValue: 0.012360075 },
    { dataValue: 0.5, alphaValue: 0.9916667 },
    { dataValue: 1.0, alphaValue: 0.9916667 },
  ],
};

export const TF_DEFAULT: TransferFunctionJson = {
  version: 1,
  colourPoints: [
    { dataValue: 0.0, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 1.0, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
  ],
  alphaPoints: [
    { dataValue: 0.0, alphaValue: 1.0 },
    { dataValue: 1.0, alphaValue: 1.0 },
  ],
};

export const TF_LEAFLETS_ONLY: TransferFunctionJson = {
  version: 1,
  colourPoints: [
    { dataValue: 0.0, colourValue: { r: 0.89, g: 0.7, b: 0.58, a: 1.0 } },
    { dataValue: 1.0, colourValue: { r: 0.89, g: 0.7, b: 0.58, a: 1.0 } },
  ],
  alphaPoints: [
    { dataValue: 0.0, alphaValue: 0.0 },
    { dataValue: 1.0, alphaValue: 0.0 },
  ],
};

export const TF_RAW_CT: TransferFunctionJson = {
  version: 1,
  colourPoints: [
    { dataValue: 0.0, colourValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 } },
    { dataValue: 0.27448107, colourValue: { r: 0.0, g: 0.0, b: 0.0, a: 1.0 } },
    { dataValue: 0.42100122, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
    { dataValue: 1.0, colourValue: { r: 1.0, g: 1.0, b: 1.0, a: 1.0 } },
  ],
  alphaPoints: [
    { dataValue: 0.0, alphaValue: 0.0 },
    { dataValue: 0.27448107, alphaValue: 0.0 },
    { dataValue: 0.42100122, alphaValue: 1.0 },
    { dataValue: 1.0, alphaValue: 1.0 },
  ],
};

export type RenderMode = 'dvr' | 'mip' | 'iso';

export interface PresetDefinition {
  name: string;
  description: string;
  tf: TransferFunctionJson;
  window: [number, number];
  boxOpacity: number;
  lighting: boolean | 'keep';
  lightingThreshold: [number, number];
  cameraClip: number; // Unity units (1 = 100 mm)
  samplingMultiplier: number;
  cubic: boolean;
  /** Root-fitted inclusive cutout box: Cusp 1/2/3 + VBR + STJ ring points, +3 mm per side. */
  rootCrop: boolean;
  /** Render the run's leaflet field on top of the volume (the desktop's leaflet overlay). */
  leafletOverlay: boolean;
  /** How the frame's clean volume is used: as the shader's second volume inside the root region
   * (Clean Root), as the bound volume itself with the run's clean TF (Emptied), or not at all. */
  cleanVolume: 'split' | 'bound' | 'none';
  /** Leaflet colour: the bound TF's own tissue texel ('tf') or an sRGB override. */
  leafletColor: 'tf' | [number, number, number];
  /** Needs run inputs that may be absent. */
  requires: ('clean_ct' | 'cusp_field' | 'isolation_mask')[];
  /** Behaviour when inputs are missing: hide the tile, or fall back to the raw look. */
  whenMissing: 'hide' | 'fallback';
}

const MIN_THRESHOLD: [number, number] = [0, 0];

/** Tile order from InitializePresets: 1 Clean Root (scene default), 2 Endocast, 3 Emptied,
 * 4 Leaflets, 5 Transparent, 6 Raw Emptied, 7 Raw CT. */
export const PRESETS: PresetDefinition[] = [
  {
    name: 'Clean Root',
    description:
      'From outside the Endocast look; from inside (camera clip / cutout through the root) the emptied look with the leaflet overlay. Needs the clean volume and cusp field; without them it renders as Endocast.',
    tf: TF_CARDIAC_VR,
    window: [0, 1],
    boxOpacity: 1,
    lighting: true,
    lightingThreshold: MIN_THRESHOLD,
    cameraClip: 0.25,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: false,
    leafletOverlay: true,
    cleanVolume: 'split',
    leafletColor: LEAFLET_TISSUE_COLOR,
    requires: ['clean_ct', 'cusp_field'],
    whenMissing: 'fallback',
  },
  {
    name: 'Endocast',
    description: 'Cardiac volume-render look: orange/red contrast-enhanced CT.',
    tf: TF_CARDIAC_VR,
    window: [0, 1],
    boxOpacity: 1,
    lighting: true,
    lightingThreshold: MIN_THRESHOLD,
    cameraClip: 0.25,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: false,
    leafletOverlay: false,
    cleanVolume: 'none',
    leafletColor: 'tf',
    requires: ['isolation_mask'],
    whenMissing: 'fallback',
  },
  {
    name: 'Emptied',
    description: 'The clean render volume with the blood pool emptied, cropped to the root.',
    tf: TF_CARDIAC_VR_CUTAWAY_CLEAN,
    window: [0, 1],
    boxOpacity: 1,
    lighting: true,
    lightingThreshold: [0.03, 0.1],
    cameraClip: 0.2,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: true,
    leafletOverlay: false,
    cleanVolume: 'bound',
    leafletColor: 'tf',
    requires: ['clean_ct'],
    whenMissing: 'hide',
  },
  {
    name: 'Leaflets',
    description: 'The leaflet surface from the cusp field alone, over a fully transparent transfer function.',
    tf: TF_LEAFLETS_ONLY,
    window: [0, 1],
    boxOpacity: 1,
    lighting: true,
    lightingThreshold: MIN_THRESHOLD,
    cameraClip: 0.25,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: false,
    leafletOverlay: true,
    cleanVolume: 'none',
    leafletColor: 'tf',
    requires: ['cusp_field'],
    whenMissing: 'hide',
  },
  {
    name: 'Transparent',
    description: 'Flat white see-through volume.',
    tf: TF_DEFAULT,
    window: [0.31, 0.33],
    boxOpacity: 0.24,
    lighting: 'keep',
    lightingThreshold: [0.02, 0.15],
    cameraClip: 0,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: false,
    leafletOverlay: false,
    cleanVolume: 'none',
    leafletColor: 'tf',
    requires: [],
    whenMissing: 'fallback',
  },
  {
    name: 'Raw Emptied',
    description: 'The raw CT through the hand-authored cutaway transfer function, cropped to the root.',
    tf: TF_CARDIAC_VR_CUTAWAY,
    window: [0, 1],
    boxOpacity: 1,
    lighting: true,
    lightingThreshold: MIN_THRESHOLD,
    cameraClip: 0.2,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: true,
    leafletOverlay: false,
    cleanVolume: 'none',
    leafletColor: 'tf',
    requires: [],
    whenMissing: 'fallback',
  },
  {
    name: 'Raw CT',
    description: 'The raw CT through a plain grayscale ramp (100..700 HU), lit.',
    tf: TF_RAW_CT,
    window: [0, 1],
    boxOpacity: 1,
    lighting: true,
    lightingThreshold: MIN_THRESHOLD,
    cameraClip: 0.25,
    samplingMultiplier: 3,
    cubic: true,
    rootCrop: false,
    leafletOverlay: false,
    cleanVolume: 'none',
    leafletColor: 'tf',
    requires: [],
    whenMissing: 'fallback',
  },
];

export const DEFAULT_PRESET = 'Clean Root';

/** Load-time defaults from VolumetricObject.Load (before the first preset is applied). */
export const LOAD_DEFAULTS = {
  samplingMultiplier: 2,
  window: [0.3, 0.37] as [number, number],
  boxOpacity: 0.24,
  lightingThreshold: [0.02, 0.15] as [number, number],
  cameraClip: 0.25,
  minGradient: 0.01,
};
